import { ASTNode, LiteralNode, NodeBase, TerminalNode } from "./ast";
import { ParsingError } from "./errors";
import { Token } from "./lexer";

// Type helper to convert symbol array to corresponding ASTNode arguments
type ASTNodeArgsFor<TSymbols> = TSymbols extends [
  infer U extends string,
  ...infer Rest extends string[],
]
  ? [ASTNode<U>, ...ASTNodeArgsFor<Rest>]
  : [];

export type NonTerminalRule<
  TNode = any,
  TSymbols extends string[] = any[],
  THandler extends (...args: ASTNodeArgsFor<TSymbols>) => ASTNode<any, any> = (
    ...args: any[]
  ) => ASTNode<any, any>,
> = TNode extends NodeBase<infer U>
  ? { name: U; symbols: TSymbols; handler: THandler }
  : never;

export type TerminalRule<
  TNode = any,
  THandler extends (token: Token<any>) => ASTNode<any, any> = (
    token: Token<any>,
  ) => ASTNode<any, any>,
> = TNode extends TerminalNode<infer U>
  ? { type: "terminal"; name: U; handler: THandler }
  : never;

export type LiteralRule<
  TNode = any,
  THandler extends (token: Token<any>) => ASTNode<any, any> = (
    token: Token<any>,
  ) => ASTNode<any, any>,
> = TNode extends LiteralNode<infer U>
  ? { type: "literal"; name: U; handler: THandler }
  : never;

// Union type for all parser rules
export type ParserRule = NonTerminalRule | TerminalRule | LiteralRule;

// Magic: infer the type map from an array of parser rules
type InferASTNodeMap<T extends readonly ParserRule[]> = {
  [K in T[number] as K["name"]]: ReturnType<K["handler"]>;
};

// Extract token types from the inferred map
type InferTokenTypes<T extends readonly ParserRule[]> = Extract<
  T[number],
  { type: "terminal" } | { symbols: any }
>["name"];

type AllSymbols<
  TTokenType extends string = string,
  TLiteral extends string = string,
> = TTokenType | TLiteral | EmptyProduction | EndOfInput | DummySymbol;

// LALR(1) item with lookahead
interface LALRItem<
  TTokenType extends string = string,
  TLiteral extends string = string,
> {
  /** The index of the grammar rule */
  ruleIndex: number;
  /** The position of the dot in the rule */
  dotIndex: number;
  /** The set of lookahead symbols */
  lookaheads: Set<AllSymbols<TTokenType, TLiteral>>;
}

type Action =
  | { type: "shift"; state: number }
  | { type: "reduce"; rule: number }
  | { type: "accept" }
  | { type: "error" };

interface ParserState<TTokenType extends string, TLiteral extends string> {
  id: number;
  kernel: LALRItem<TTokenType, TLiteral>[]; // Core items (without closure)
  items: LALRItem<TTokenType, TLiteral>[]; // All items (with closure)
  transitions: Map<AllSymbols<TTokenType, TLiteral>, number>; // symbol -> next state
}

const END_OF_INPUT = "$";
type EndOfInput = typeof END_OF_INPUT;

const EMPTY_PRODUCTION = "ε";
type EmptyProduction = typeof EMPTY_PRODUCTION;

const DUMMY_SYMBOL = "#";
type DummySymbol = typeof DUMMY_SYMBOL;

// For tracking lookahead propagation
interface LookaheadLink {
  fromState: number;
  fromItem: number;
  toState: number;
  toItem: number;
}

export interface IParser<T extends readonly ParserRule[]> {
  parse(tokens: Token<string>[]): InferASTNodeMap<T>[InferTokenTypes<T>];
  printTables(): void;
}

class Parser<const T extends readonly ParserRule[]> implements IParser<T> {
  private nonTerminalRules: NonTerminalRule[];
  private terminalRules: Map<string, TerminalRule["handler"]>;
  private literalRules: Map<string, LiteralRule["handler"]>;

  private terminalSymbols: Set<string> = new Set();
  private nonTerminalSymbols: Set<string> = new Set();
  private literalSymbols: Set<string> = new Set();
  private states: ParserState<string, string>[] = [];
  private actionTable: Map<string, Action> = new Map();
  private gotoTable: Map<string, number> = new Map();

  private firstSets: Map<string, Set<string>> = new Map();
  private followSets: Map<string, Set<string>> = new Map();

  constructor(
    private grammar: T,
    private startSymbol: InferTokenTypes<T>,
  ) {
    this.nonTerminalRules = this.extractNonTerminalRules(grammar as any);
    this.terminalRules = this.extractTerminalRules(grammar as any);
    this.literalRules = this.extractLiteralRules(grammar as any);

    this.extractSymbols();
    this.buildParsingTables();
  }

  private extractNonTerminalRules(
    grammar: readonly ParserRule[],
  ): NonTerminalRule[] {
    return grammar.filter(isNonTerminalRule);
  }

  private extractTerminalRules(
    grammar: readonly ParserRule[],
  ): Map<string, TerminalRule["handler"]> {
    return new Map(
      grammar
        .filter(isTerminalRule)
        .map(({ name, handler }) => [name, handler]),
    );
  }

  private extractLiteralRules(
    grammar: readonly ParserRule[],
  ): Map<string, LiteralRule["handler"]> {
    return new Map(
      grammar.filter(isLiteralRule).map(({ name, handler }) => [name, handler]),
    );
  }

  private get augmentedRules(): readonly NonTerminalRule[] {
    return [
      {
        name: this.augmentedStart,
        symbols: [this.startSymbol],
        handler: (nextSymbol: ASTNode<any, any>) => ({
          type: this.augmentedStart,
          children: [nextSymbol],
        }),
      },
      ...this.nonTerminalRules,
    ];
  }

  private get augmentedStart(): string {
    return `${this.startSymbol}'`;
  }

  private extractSymbols(): void {
    // Extract terminal and non-terminal symbols from augmented grammar
    this.nonTerminalSymbols.add(this.augmentedStart);

    // Add all rule names to appropriate symbol sets
    for (const rule of this.grammar) {
      if (isTerminalRule(rule)) {
        this.terminalSymbols.add(rule.name);
      } else if (isNonTerminalRule(rule)) {
        this.nonTerminalSymbols.add(rule.name);
      } else if (isLiteralRule(rule)) {
        this.literalSymbols.add(rule.name);
        this.terminalSymbols.add(rule.name);
      }
    }

    // Extract symbols from rule bodies
    for (const rule of this.augmentedRules) {
      for (const symbol of rule.symbols) {
        if (this.isLiteral(symbol)) {
          this.literalSymbols.add(symbol);
          this.terminalSymbols.add(symbol);
        } else if (!this.isNonTerminal(symbol)) {
          this.terminalSymbols.add(symbol);
        }
      }
    }

    this.terminalSymbols.add(END_OF_INPUT);
  }

  private isNonTerminal(symbol: string): boolean {
    return this.nonTerminalSymbols.has(symbol);
  }

  private isLiteral(symbol: string): boolean {
    return this.literalSymbols.has(symbol);
  }

  private computeFirstSets(): void {
    // Initialize FIRST sets for all symbols
    for (const symbol of this.nonTerminalSymbols) {
      this.firstSets.set(symbol, new Set());
    }

    for (const symbol of this.terminalSymbols) {
      this.firstSets.set(symbol, new Set([symbol])); // Terminals contain themselves
    }

    let changed = true;
    while (changed) {
      changed = false;
      for (const rule of this.augmentedRules) {
        const firstSet = this.firstSets.get(rule.name);
        if (!firstSet) continue;

        const oldSize = firstSet.size;

        if (rule.symbols.length === 0) {
          // Empty production
          firstSet.add(EMPTY_PRODUCTION);
        } else {
          // Process each symbol in RHS
          let allHaveEpsilon = true;
          for (const symbol of rule.symbols) {
            const symbolFirst = this.firstSets.get(symbol);

            // Ensure the symbol has a FIRST set
            if (!symbolFirst) {
              // If symbol not found, it might be a terminal we missed
              if (!this.firstSets.has(symbol)) {
                this.firstSets.set(symbol, new Set([symbol]));
                this.terminalSymbols.add(symbol);
              }
              continue;
            }

            // Add all non-epsilon symbols
            for (const sym of symbolFirst) {
              if (sym !== EMPTY_PRODUCTION) {
                firstSet.add(sym);
              }
            }

            // If this symbol doesn't have epsilon, stop
            if (!symbolFirst.has(EMPTY_PRODUCTION)) {
              allHaveEpsilon = false;
              break;
            }
          }

          // If all symbols can derive epsilon, add epsilon
          if (allHaveEpsilon) {
            firstSet.add(EMPTY_PRODUCTION);
          }
        }

        if (firstSet.size > oldSize) changed = true;
      }
    }
  }

  private computeFollowSets(): void {
    // Initialize FOLLOW sets
    for (const nt of this.nonTerminalSymbols) {
      this.followSets.set(nt, new Set());
    }

    let changed = true;
    while (changed) {
      changed = false;
      for (const rule of this.augmentedRules) {
        for (const [i, symbol] of rule.symbols.entries()) {
          if (this.isNonTerminal(symbol)) {
            const followSet = this.followSets.get(symbol);
            if (!followSet) continue;

            const oldSize = followSet?.size;

            // Compute FIRST of beta (what follows this symbol)
            const beta = rule.symbols.slice(i + 1);
            const firstBeta = this.computeFirstOfString(beta);

            // Add FIRST(beta) - {ε} to FOLLOW(symbol)
            for (const sym of firstBeta) {
              if (sym !== EMPTY_PRODUCTION) {
                followSet.add(sym);
              }
            }

            // If ε ∈ FIRST(beta), add FOLLOW(A) to FOLLOW(symbol)
            if (firstBeta.has(EMPTY_PRODUCTION)) {
              const lhsFollow = this.followSets.get(rule.name);
              if (!lhsFollow) continue;

              for (const sym of lhsFollow) {
                followSet.add(sym);
              }
            }

            if (followSet.size > oldSize) {
              changed = true;
            }
          }
        }
      }
    }
  }

  private computeFirstOfString(symbols: string[]): Set<string> {
    const result = new Set<string>();

    if (symbols.length === 0) {
      result.add(EMPTY_PRODUCTION);
      return result;
    }

    let allHaveEpsilon = true;
    for (const symbol of symbols) {
      const firstSet = this.firstSets.get(symbol);

      // Ensure the symbol has a FIRST set
      if (!firstSet) {
        // If it's not in our sets, treat it as a terminal
        result.add(symbol);
        allHaveEpsilon = false;
        break;
      }

      // Add all non-epsilon symbols
      for (const sym of firstSet) {
        if (sym !== EMPTY_PRODUCTION) {
          result.add(sym);
        }
      }

      // If this symbol doesn't have epsilon, stop
      if (!firstSet.has(EMPTY_PRODUCTION) || this.isLiteral(symbol)) {
        allHaveEpsilon = false;
        break;
      }
    }

    // If all symbols can derive epsilon, add epsilon
    if (allHaveEpsilon) {
      result.add(EMPTY_PRODUCTION);
    }

    return result;
  }

  private closure(
    items: LALRItem<string, string>[],
  ): LALRItem<string, string>[] {
    const result = [...items];
    const itemMap = new Map<string, LALRItem<string, string>>();

    // Index existing items
    for (const item of result) {
      const key = `${item.ruleIndex}-${item.dotIndex}`;
      itemMap.set(key, item);
    }

    for (const item of result) {
      const rule = this.augmentedRules[item.ruleIndex];

      if (rule && item.dotIndex < rule.symbols.length) {
        const nextSymbol = rule.symbols[item.dotIndex];
        if (nextSymbol !== undefined && this.isNonTerminal(nextSymbol)) {
          // Compute FIRST of β α where β is what follows nextSymbol and α is lookahead
          const beta = rule.symbols.slice(item.dotIndex + 1);

          for (const lookahead of item.lookaheads) {
            const firstBetaAlpha = this.computeFirstOfString([
              ...beta,
              lookahead,
            ]);

            // Add items for all productions of nextSymbol
            for (const [j, augmentedRule] of this.augmentedRules.entries()) {
              if (augmentedRule.name === nextSymbol) {
                const key = `${j}-0`;
                let newItem = itemMap.get(key);

                if (!newItem) {
                  newItem = {
                    ruleIndex: j,
                    dotIndex: 0,
                    lookaheads: new Set(),
                  };
                  result.push(newItem);
                  itemMap.set(key, newItem);
                }

                // Add computed lookaheads
                for (const la of firstBetaAlpha) {
                  if (la !== EMPTY_PRODUCTION) {
                    newItem.lookaheads.add(la);
                  }
                }
              }
            }
          }
        }
      }
    }

    return result;
  }

  private goto(
    items: LALRItem<string, string>[],
    symbol: string,
  ): LALRItem<string, string>[] {
    const result: LALRItem<string, string>[] = [];
    const itemMap = new Map<string, LALRItem<string, string>>();

    for (const item of items) {
      const rule = this.augmentedRules[item.ruleIndex];
      if (
        rule &&
        item.dotIndex < rule.symbols.length &&
        rule.symbols[item.dotIndex] === symbol
      ) {
        const key = `${item.ruleIndex}-${item.dotIndex + 1}`;
        let newItem = itemMap.get(key);

        if (!newItem) {
          newItem = {
            ruleIndex: item.ruleIndex,
            dotIndex: item.dotIndex + 1,
            lookaheads: new Set(),
          };
          result.push(newItem);
          itemMap.set(key, newItem);
        }

        // Copy lookaheads
        for (const la of item.lookaheads) {
          newItem.lookaheads.add(la);
        }
      }
    }

    return this.closure(result);
  }

  private buildLR0States(): void {
    // Start with kernel item S' -> •S with lookahead $
    const startItem: LALRItem<string, string> = {
      ruleIndex: 0,
      dotIndex: 0,
      lookaheads: new Set([END_OF_INPUT]),
    };

    const startKernel = [startItem];
    const startItems = this.closure(startKernel);

    this.states = [
      {
        id: 0,
        kernel: startKernel,
        items: startItems,
        transitions: new Map(),
      },
    ];

    const stateMap = new Map<string, number>();
    stateMap.set(this.kernelToString(startKernel), 0);

    for (const state of this.states) {
      const symbols = new Set<string>();

      // Collect all symbols that can be shifted
      for (const item of state.items) {
        const rule = this.augmentedRules[item.ruleIndex];
        const symbol = rule?.symbols[item.dotIndex];
        if (symbol) {
          symbols.add(symbol);
        }
      }

      // Build transitions
      for (const symbol of symbols) {
        const newKernel = this.goto(state.items, symbol);
        if (newKernel.length > 0) {
          // Extract just the kernel (core) items
          const kernelItems = this.extractKernel(newKernel);
          const kernelKey = this.kernelToString(kernelItems);
          let nextStateId = stateMap.get(kernelKey);

          if (nextStateId === undefined) {
            nextStateId = this.states.length;
            this.states.push({
              id: nextStateId,
              kernel: kernelItems,
              items: newKernel,
              transitions: new Map(),
            });
            stateMap.set(kernelKey, nextStateId);
          }

          state.transitions.set(symbol, nextStateId);
        }
      }
    }
  }

  private extractKernel(
    items: LALRItem<string, string>[],
  ): LALRItem<string, string>[] {
    // Kernel items are either:
    // 1. The initial item S' -> •S
    // 2. Items with dot > 0
    return items.filter(
      (item) =>
        (item.ruleIndex === 0 && item.dotIndex === 0) || item.dotIndex > 0,
    );
  }

  private kernelToString(kernel: LALRItem[]): string {
    return kernel
      .map((item) => `${item.ruleIndex}:${item.dotIndex}`)
      .sort()
      .join(",");
  }

  private computeLALRLookaheads(): void {
    // First, build LR(0) states with spontaneous lookaheads
    const propagationLinks: LookaheadLink[] = [];

    // For each state, determine lookahead propagation
    for (const state of this.states) {
      for (const [i, item] of state.kernel.entries()) {
        // Create item with dummy lookahead
        const testItem: LALRItem<string, string> = {
          ruleIndex: item.ruleIndex,
          dotIndex: item.dotIndex,
          lookaheads: new Set([DUMMY_SYMBOL]),
        };

        const closure = this.closure([testItem]);

        // Check each item in closure
        for (const closureItem of closure) {
          const rule = this.augmentedRules[closureItem.ruleIndex];
          const symbol = rule?.symbols[closureItem.dotIndex];
          if (symbol) {
            const nextStateId = state.transitions.get(symbol);
            const nextState =
              nextStateId === undefined ? undefined : this.states[nextStateId];

            if (nextState) {
              const gotoItem: LALRItem<string, string> = {
                ruleIndex: closureItem.ruleIndex,
                dotIndex: closureItem.dotIndex + 1,
                lookaheads: new Set(),
              };

              // Find corresponding item in next state's kernel
              for (const [j, kernelItem] of nextState.kernel.entries()) {
                if (
                  kernelItem.ruleIndex === gotoItem.ruleIndex &&
                  kernelItem.dotIndex === gotoItem.dotIndex
                ) {
                  // Check if lookaheads should propagate
                  if (closureItem.lookaheads.has(DUMMY_SYMBOL)) {
                    // Propagate from current item
                    propagationLinks.push({
                      fromState: state.id,
                      fromItem: i,
                      toState: nextState.id,
                      toItem: j,
                    });
                  }

                  // Add spontaneous lookaheads
                  for (const la of closureItem.lookaheads) {
                    if (la !== DUMMY_SYMBOL) {
                      kernelItem.lookaheads.add(la);
                    }
                  }
                  break;
                }
              }
            }
          }
        }
      }
    }

    // Propagate lookaheads until convergence
    let changed = true;
    while (changed) {
      changed = false;
      for (const link of propagationLinks) {
        const fromItem = this.states[link.fromState]?.kernel[link.fromItem];
        const toItem = this.states[link.toState]?.kernel[link.toItem];
        const oldSize = toItem?.lookaheads.size;

        if (fromItem && toItem) {
          for (const la of fromItem.lookaheads) {
            toItem.lookaheads.add(la);
          }
        }

        if (
          toItem &&
          oldSize !== undefined &&
          toItem.lookaheads.size > oldSize
        ) {
          changed = true;
        }
      }
    }

    // Update closure items with computed lookaheads
    for (const state of this.states) {
      state.items = this.closure(state.kernel);
    }
  }

  private buildParsingTables(): void {
    this.computeFirstSets();
    this.computeFollowSets();
    this.buildLR0States();
    this.computeLALRLookaheads();

    // Build ACTION and GOTO tables
    this.buildActionTable();
    this.buildGotoTable();
  }

  private buildActionTable(): void {
    for (const state of this.states) {
      for (const item of state.items) {
        const rule = this.augmentedRules[item.ruleIndex];

        if (rule && item.dotIndex < rule.symbols.length) {
          // Shift action
          const symbol = rule.symbols[item.dotIndex];
          if (symbol !== undefined && this.terminalSymbols.has(symbol)) {
            const nextState = state.transitions.get(symbol);
            if (nextState !== undefined) {
              const key = `${state.id},${symbol}`;
              const existingAction = this.actionTable.get(key);

              if (existingAction && existingAction.type !== "shift") {
                throw new ParsingError(
                  `Shift/Reduce conflict in state ${state.id} on symbol ${symbol}`,
                );
              }

              this.actionTable.set(key, {
                type: "shift",
                state: nextState,
              });
            }
          }
        } else {
          // Reduce action
          if (item.ruleIndex === 0) {
            // Accept action for S' -> S •
            this.actionTable.set(`${state.id},$`, { type: "accept" });
          } else {
            // Reduce action for specific lookaheads
            for (const lookahead of item.lookaheads) {
              const key = `${state.id},${lookahead}`;
              const existingAction = this.actionTable.get(key);

              if (existingAction) {
                if (existingAction.type === "shift") {
                  throw new ParsingError(
                    `Shift/Reduce conflict in state ${state.id} on symbol ${lookahead}`,
                  );
                } else if (
                  existingAction.type === "reduce" &&
                  existingAction.rule !== item.ruleIndex
                ) {
                  throw new ParsingError(
                    `Reduce/Reduce conflict in state ${state.id} on symbol ${lookahead}`,
                  );
                }
              }

              this.actionTable.set(key, {
                type: "reduce",
                rule: item.ruleIndex,
              });
            }
          }
        }
      }
    }
  }

  private buildGotoTable(): void {
    for (const state of this.states) {
      for (const [symbol, nextState] of state.transitions) {
        if (this.isNonTerminal(symbol)) {
          this.gotoTable.set(`${state.id},${symbol}`, nextState);
        }
      }
    }
  }

  // Updated parse method with strong typing
  public parse(
    tokens: Token<string>[],
  ): InferASTNodeMap<T>[InferTokenTypes<T>] {
    const stack: { state: number; node?: ASTNode<string, string> }[] = [
      { state: 0 },
    ];

    let index = 0;

    while (true) {
      const currentState = stack.at(-1)?.state;
      const currentToken: Token<string | EndOfInput> = tokens[index] ?? {
        name: END_OF_INPUT,
        text: "",
        offset: 0,
        len: 0,
        state: "",
        line: 0,
        column: 0,
      };

      const tokenSymbol = this.getSymbolForToken(currentToken);
      const action = this.actionTable.get(`${currentState},${tokenSymbol}`);

      if (!action) {
        throw new ParsingError(
          `Parse error at token "${currentToken.name}" with text: "${currentToken.text}"`,
        );
      }

      switch (action.type) {
        case "shift": {
          // Check if there's a custom terminal handler
          const terminalHandler = this.terminalRules.get(currentToken.name);
          const literalHandler = this.literalRules.get(currentToken.name);

          if (!isNonTerminalToken(currentToken)) {
            throw new ParsingError(
              `Unexpectedly shifting "${END_OF_INPUT}" onto the stack.`,
            );
          }

          const shiftNode: ASTNode<string, string> = terminalHandler
            ? terminalHandler.call(this, currentToken as Token<string>)
            : literalHandler
              ? literalHandler.call(this, currentToken as Token<string>)
              : ({
                  type: currentToken.name,
                  value: currentToken.text,
                  token: currentToken,
                } as ASTNode<string, string>);

          stack.push({ state: action.state, node: shiftNode });
          index++;
          break;
        }
        case "reduce": {
          const rule = this.augmentedRules[action.rule];
          if (!rule) {
            continue;
          }

          const children: ASTNode<string, string>[] = [];

          // Pop RHS symbols from stack
          for (let i = 0; i < rule.symbols.length; i++) {
            const popped = stack.pop();
            if (popped?.node) {
              children.unshift(popped.node);
            }
          }

          // Create new AST node - the handler is properly typed!
          const newNode: ASTNode<string, string> = rule.handler.apply(
            this,
            children,
          );

          // Get goto state
          const newState = stack.at(-1)?.state;
          const gotoState = this.gotoTable.get(`${newState},${rule.name}`);

          if (gotoState === undefined) {
            throw new ParsingError(
              `No goto entry for state ${newState}, symbol ${rule.name}`,
            );
          }

          stack.push({ state: gotoState, node: newNode });
          break;
        }
        case "accept": {
          const result = stack.at(-1)?.node;
          if (!result) {
            throw new ParsingError("Parse completed but no AST node found");
          }
          return result as InferASTNodeMap<T>[InferTokenTypes<T>];
        }
        case "error": {
          throw new ParsingError(`Parse error at token "${currentToken.text}"`);
        }
      }
    }
  }

  private getSymbolForToken(token: Token<string>): string {
    // For literal tokens (like math operators or parentheses), use the text content
    if (this.literalRules.has(token.name)) {
      return token.text;
    }

    // For terminal tokens (like numbers), use the name
    if (this.terminalRules.has(token.name)) {
      return token.name;
    }

    // Fallback to name
    return token.name;
  }

  // Debug method to print parsing tables
  public printTables(): void {
    for (const state of this.states) {
      for (const item of state.items) {
        const rule = this.augmentedRules[item.ruleIndex];
        if (!rule) {
          continue;
        }

        const symbols: string[] = [...rule.symbols];
        symbols.splice(item.dotIndex, 0, "•");
        const _lookaheads = [...item.lookaheads].join(", ");
      }
    }
    for (const [_key, _action] of this.actionTable) {
    }
    for (const [_key, _state] of this.gotoTable) {
    }
  }
}

// Helper functions with explicit type checking for new rule system
function isNonTerminalRule(rule: ParserRule): rule is NonTerminalRule {
  return "symbols" in rule && !("type" in rule);
}

function isTerminalRule(rule: ParserRule): rule is TerminalRule {
  return "type" in rule && rule.type === "terminal";
}

function isLiteralRule(rule: ParserRule): rule is LiteralRule {
  return "type" in rule && rule.type === "literal";
}

function isNonTerminalToken<TTokenType extends string>(
  token: Token<TTokenType | EndOfInput>,
): token is Token<TTokenType> {
  return token.name !== END_OF_INPUT;
}

// Helper function to create parsers with proper type inference
export function createParser<const T extends readonly ParserRule[]>(
  grammar: T,
  startSymbol: InferTokenTypes<T>,
): IParser<T> {
  return new Parser(grammar, startSymbol);
}
